import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserCheck, UserPlus } from 'lucide-react';

import { useToastStore } from '../../../stores/toastStore';
import { setDocumentAssignee, type DocumentResultResponse } from '../api';
import { reviewerQueryKeys } from '../hooks/useDocumentResult';

interface AssigneeControlProps {
  jobId: string;
  assignedTo: string | null;
  /** The logged-in user's id, for "assign to me" and "you" rendering; null if unknown. */
  currentUserId: string | null;
}

/**
 * Header control to assign/reassign/unassign the document under review (J1). Shows the current
 * assignee and a small menu: assign to me, assign to someone else (free-typed user id/email, since
 * there is no tenant user directory), or unassign. Writes through `setDocumentAssignee` and replaces
 * the cached result with the server's refreshed payload.
 */
export function AssigneeControl({ jobId, assignedTo, currentUserId }: AssigneeControlProps): JSX.Element {
  const queryClient = useQueryClient();
  const pushToast = useToastStore((s) => s.push);
  const [open, setOpen] = useState(false);
  const [typing, setTyping] = useState(false);
  const [value, setValue] = useState('');

  const mutation = useMutation({
    mutationFn: (assignee: string | null) => setDocumentAssignee(jobId, assignee),
    onSuccess: (updated: DocumentResultResponse) => {
      queryClient.setQueryData(reviewerQueryKeys.result(jobId), updated);
      setOpen(false);
      setTyping(false);
      setValue('');
      pushToast({
        tone: 'success',
        title: updated.assignedTo === null || updated.assignedTo === undefined ? 'Asignación retirada' : 'Documento asignado',
        ...(updated.assignedTo ? { description: updated.assignedTo } : {}),
      });
    },
    onError: (e: unknown) =>
      pushToast({ tone: 'error', title: 'No se pudo cambiar la asignación', description: e instanceof Error ? e.message : '' }),
  });

  const assignedToMe = assignedTo !== null && assignedTo === currentUserId;
  const label = assignedTo === null ? 'Sin asignar' : assignedToMe ? 'Asignado a ti' : assignedTo;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="btn-secondary inline-flex items-center gap-1"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="assignee-button"
      >
        {assignedTo === null ? (
          <UserPlus className="h-4 w-4" aria-hidden strokeWidth={2} />
        ) : (
          <UserCheck className="h-4 w-4 text-primary" aria-hidden strokeWidth={2} />
        )}
        <span className="max-w-[10rem] truncate">{label}</span>
      </button>

      {open ? (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => { setOpen(false); setTyping(false); }}
          />
          <div
            role="menu"
            className="absolute right-0 z-50 mt-1 w-60 rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
          >
            {currentUserId !== null && !assignedToMe ? (
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                onClick={() => mutation.mutate(currentUserId)}
                disabled={mutation.isPending}
                data-testid="assignee-assign-me"
              >
                <UserCheck className="h-4 w-4" aria-hidden strokeWidth={2} />
                Asignármelo
              </button>
            ) : null}

            {typing ? (
              <form
                className="px-2 py-1.5"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (value.trim() !== '') {
                    mutation.mutate(value.trim());
                  }
                }}
              >
                <input
                  className="input"
                  autoFocus
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="usuario@empresa.com"
                  aria-label="Asignar a usuario"
                  data-testid="assignee-input"
                />
                <button
                  type="submit"
                  className="btn-primary mt-2 w-full px-3 py-1.5 text-xs"
                  disabled={mutation.isPending || value.trim() === ''}
                  data-testid="assignee-confirm"
                >
                  Asignar
                </button>
              </form>
            ) : (
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() => setTyping(true)}
                data-testid="assignee-assign-other"
              >
                <UserPlus className="h-4 w-4" aria-hidden strokeWidth={2} />
                Asignar a…
              </button>
            )}

            {assignedTo !== null ? (
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-sm text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                onClick={() => mutation.mutate(null)}
                disabled={mutation.isPending}
                data-testid="assignee-unassign"
              >
                Quitar asignación
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
